// frontend/src/components/ImpersonationBanner.js
//
// A persistent red banner shown across the whole tenant app whenever the
// current session is a TALIT platform support session (impersonate /
// full_access / shadow) rather than a normal staff login. See
// docs/platform-admin-architecture.md §4.
//
// Detection is purely a DISPLAY read of the session JWT — decodeJwtPayload
// does no signature check (api/jwt.js's header spells this out). That is
// fine here: the banner is a courtesy, not a security control. The real
// enforcement (session validity, expiry, tenant revocation) is entirely
// server-side in worker/lib/auth.js's authenticate().
//
// The "End session" button revokes every active grant for this tenant,
// which server-side force-ends any session bound to it on the next request.
// A tenant staff member always has this escape hatch.
import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, X } from 'lucide-react';
import { decodeJwtPayload } from '../api/jwt';
import { apiFetch } from '../api/client';

function fmtCountdown(secondsLeft) {
  if (secondsLeft <= 0) return 'expired';
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ImpersonationBanner() {
  const [payload, setPayload] = useState(() => decodeJwtPayload(localStorage.getItem('token')));
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [ending, setEnding] = useState(false);

  // Re-read the token if it changes (e.g. after login) and tick a countdown.
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
      const p = decodeJwtPayload(localStorage.getItem('token'));
      setPayload((prev) => (JSON.stringify(prev) === JSON.stringify(p) ? prev : p));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // A ghost session (docs §4.4) is invisible to the tenant BY DESIGN — no
  // banner. The server already withholds it from the tenant's support-log
  // and active-sessions list; this is the matching client-side omission.
  const isSupport =
    !!payload?.acting_as_admin_id &&
    !!payload?.pa_session_id &&
    payload?.mode !== 'ghost';

  const endSession = useCallback(async () => {
    setEnding(true);
    try {
      // Fetch the active grant(s) and revoke them — that's the tenant's
      // lever. If there is no grant (owner-role self-authorized full_access),
      // there is nothing tenant-side to revoke; the operator or the TTL
      // ends it. We still try and just reload.
      const res = await apiFetch('/tenant/support-access');
      const data = await res.json().catch(() => ({}));
      const grants = [
        ...(data.activeGrant ? [data.activeGrant] : []),
        ...((data.history || []).filter((g) => g.status === 'active')),
      ];
      for (const g of grants) {
        await apiFetch(`/tenant/support-access/${g.id}`, { method: 'DELETE' });
      }
    } catch {
      /* best effort */
    } finally {
      window.location.reload();
    }
  }, []);

  if (!isSupport) return null;

  const mode = payload.mode || 'impersonate';
  const label =
    mode === 'full_access' ? 'has full access to this account'
    : mode === 'shadow' ? 'is viewing this account (read-only)'
    : 'is acting as this account';
  const secondsLeft = payload.exp ? payload.exp - now : 0;

  return (
    <div className="w-full bg-red-600 text-white text-sm flex items-center justify-between gap-3 px-4 py-2 z-[60]">
      <span className="flex items-center gap-2 min-w-0">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        <span className="truncate">
          <strong>TALIT support {label}.</strong>
          {' '}Ends in {fmtCountdown(secondsLeft)}.
          {payload.mode !== 'shadow' && ' All actions are logged.'}
        </span>
      </span>
      <button
        onClick={endSession}
        disabled={ending}
        className="shrink-0 inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded px-2 py-1 text-xs font-medium disabled:opacity-60"
      >
        <X className="w-3.5 h-3.5" />
        {ending ? 'Ending…' : 'End session'}
      </button>
    </div>
  );
}
